import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import EmptyState from '../../Common/EmptyState';
import adminApi from '../../../utils/admin.api';
import ConfirmDialog from '../../Common/ConfirmDialog';
import UserTable from './UserTable';
import EditUserDialog from './EditUserDialog';
import AddUserDialog from './AddUserDialog';
import BulkImportDialog from './BulkImportDialog';

const UserManagement = () => {
  const currentUserEmail = (() => {
    try {
      return JSON.parse(localStorage.getItem('UserDetails'))?.user?.companyEmail ?? '';
    } catch { return ''; }
  })();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Add
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  // Bulk import
  const [bulkOpen, setBulkOpen] = useState(false);

  // Edit
  const [editTarget, setEditTarget] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Deactivate
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Read ─────────────────────────────────────────────────────────────────────

  const loadUsers = useCallback(() => {
    setLoading(true);
    adminApi
      .getUsers(page + 1, rowsPerPage, roleFilter)
      .then(({ data }) => {
        setUsers(Array.isArray(data?.data?.items) ? data.data.items : []);
        setTotal(data?.data?.total ?? 0);
      })
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRoleFilterChange = (newRole) => {
    setRoleFilter(newRole);
    setPage(0);
  };

  // ── Create ────────────────────────────────────────────────────────────────────

  const handleCreate = async ({ name, companyEmail, password, role, is_active }) => {
    setAddSaving(true);
    setError('');
    try {
      await adminApi.createUser({
        name,
        companyEmail,
        password,
        role,
        status: is_active ? 'active' : 'inactive',
      });
      setAddOpen(false);
      loadUsers();
      showSnack('User created successfully.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create user.');
    } finally {
      setAddSaving(false);
    }
  };

  // ── Update ────────────────────────────────────────────────────────────────────

  const handleSaveEdit = async ({ name, role, is_active }) => {
    setEditSaving(true);
    setError('');
    try {
      await adminApi.updateUser(editTarget.companyEmail, {
        name,
        role,
        status: is_active ? 'active' : 'inactive',
      });
      setEditTarget(null);
      loadUsers();
      showSnack('User updated successfully.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Update failed.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Deactivate ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.status === 'inactive') {
      showSnack('User is already deactivated.');
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await adminApi.deleteUser(deleteTarget.companyEmail);
      setDeleteTarget(null);
      loadUsers();
      showSnack('User deactivated successfully.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Deactivation failed.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>User Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<GroupAddIcon />} onClick={() => setBulkOpen(true)}>
            Import Users
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button size="small" onClick={loadUsers}>Retry</Button>
        </Box>
      )}

      {!loading && total === 0 ? (
        <EmptyState title="No users found" message="No users match the current filter." />
      ) : (
        <UserTable
          users={users}
          loading={loading}
          onEdit={(u) => setEditTarget(u)}
          onDelete={(u) => setDeleteTarget(u)}
          roleFilter={roleFilter}
          onRoleFilterChange={handleRoleFilterChange}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          currentUserEmail={currentUserEmail}
        />
      )}

      {/* Add */}
      <AddUserDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleCreate}
        saving={addSaving}
      />

      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onComplete={(payload) => {
          if (payload?.summary?.created > 0) {
            loadUsers();
            showSnack(
              `Import complete: ${payload.summary.created} user(s) created.`,
            );
          }
        }}
      />

      {/* Edit */}
      <EditUserDialog
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        user={editTarget}
        onSave={handleSaveEdit}
        saving={editSaving}
        isSelf={editTarget?.companyEmail === currentUserEmail}
      />

      {/* Deactivate */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Deactivate User"
        message={`Deactivate user "${deleteTarget?.name || deleteTarget?.companyEmail}"? They will no longer be able to log in.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
